import Facebook from '@/assets/images/facebook.svg';
import Instagram from '@/assets/images/instagram.svg';
import Twitter from '@/assets/images/twitter.svg';
import Youtube from '@/assets/images/youtube.svg';
import CardWrapper from '@/components/common/CardWrapper/CardWrapper';
import { CustomDialog } from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { GET_USER_BY_ID } from '@/Graphql/queries';
import useLogout from '@/hooks/useLogout';
import { cn, getAgeRangeText } from '@/lib/utils';
import { getRoutes } from '@/routes/routePaths';
import { Gender, HeightType, Role, User, WeightType } from '@/Types/gql/graphql';
import { authState } from '@/vars/auth';
import { useQuery, useReactiveVar } from '@apollo/client';
import { capitalize, round } from 'lodash';
import { LogOut, Pencil, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { useNavigate } from 'react-router-dom';
import DeleteAccount from './components/DeleteAccount';
import TrainerProfile from './components/TrainerProfile';

export default function Profile() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { userDetails } = useReactiveVar(authState);

  const [isExpanded, setIsExpanded] = useState(false);
  const charLimit = 500;
  const { data, loading } = useQuery(GET_USER_BY_ID, { variables: { id: userDetails?._id ?? '' } });
  const user = data?.getUserById;

  const about =
    userDetails?.role === Role.Trainer || userDetails?.role === Role.Coach ? user?.about || '' : '';
  const shouldShowButton = (user?.about || '').length > charLimit;
  const toggleExpand = () => setIsExpanded((prev) => !prev);
  const { logout } = useLogout();
  const renderAboutText = () => {
    const text = isExpanded ? about : about?.substring(0, charLimit);
    return text?.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ));
  };

  return (
    <div className='bg-[#00001C]'>
      <div className='max-w-[95%] w-[1060px] mx-auto mt-14 space-y-6'>
        <div className='flex flex-col items-center mb-8'>
          <div
            className='relative'
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Avatar className='w-32 h-32 border-4 border-[#0a0b1a] bg-[#515764]'>
              {loading ? (
                <Skeleton className='w-32 h-32' />
              ) : (
                <>
                  <AvatarImage
                    src={user?.profileImage || ''}
                    alt='Profile picture'
                    className='object-cover'
                  />
                  <AvatarFallback className='text-3xl font-bold'>
                    {user?.firstName?.charAt(0) || '' + user?.lastName?.charAt(0) || ''}
                  </AvatarFallback>
                </>
              )}
            </Avatar>
            {userDetails?.role === Role.Trainee && (
              <Button
                id='image-upload-btn'
                variant='ghost'
                className={cn(
                  'hidden group-hover:flex items-center justify-center w-8 h-8 absolute top-2 right-0 rounded-full p-0 bg-bgSecondary text-white',
                  isHovered ? 'flex' : '',
                )}
                type='button'
                onClick={() => navigate(getRoutes.EditProfile, { state: { form: 1 } })}
              >
                <Pencil className='w-4 h-4' />
              </Button>
            )}
          </div>
          {loading ? (
            <Skeleton className='w-40 h-5 mt-5' />
          ) : (
            <>
              <h1 className='text-[20px] font-bold'>
                {user?.displayName ? user?.displayName : user?.firstName + ' ' + user?.lastName}
              </h1>
              {(userDetails?.role === Role.Trainer || userDetails?.role === Role.Coach) && (
                <div className='flex flex-col items-center gap-2'>
                  <span className='text-base text-[#A8ADB9] font-semibold'>
                    {user?.countryCode??''} {userDetails?.phoneNumber}
                  </span>
                  <span className='text-base text-[#A8ADB9]  font-semibold '>
                    {userDetails?.role === Role.Trainer ? 'Trainer' : 'Coach'}
                  </span>
                  <span className='flex flex-row gap-5 items-center pb-5 pt-2'>
                    <SocialMediaLinks socialMediaLinks={user?.socialMediaLinks || []} />
                  </span>
                  <span className='text-base text-[#A8ADB9] min-h-[100px]'>
                    {renderAboutText()}
                    {shouldShowButton && !isExpanded && ''}
                  </span>
                  {shouldShowButton && (
                    <div className='flex justify-end w-full'>
                      <button onClick={toggleExpand} className='text-blue-500 mt-2 text-[12px]'>
                        {isExpanded ? 'Show Less' : 'Show More'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {userDetails?.role === Role.Trainee ? (
          <>
            <CardWrapper className='py-6 px-10'>
              <CardHeader className='flex flex-row items-center justify-between p-0'>
                <h4 className='font-medium text-white'>
                  Personal Information and Physical Attributes
                </h4>
                <Button
                  id='edit-profile-btn'
                  size='default'
                  variant='secondary'
                  className='flex items-center gap-1 w-20'
                  disabled={loading}
                  onClick={() => navigate(getRoutes.EditProfile, { state: { form: 1 } })}
                >
                  <Pencil className='w-4 h-4 text-white' />
                  Edit
                </Button>
              </CardHeader>
              <CardContent className='p-0'>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                  <InfoItems
                    title='Name'
                    value={
                      user?.displayName ? user.displayName : `${user?.firstName} ${user?.lastName}`
                    }
                    loading={loading}
                  />
                  <InfoItems
                    title='Age'
                    value={user?.ageRange ? getAgeRangeText(user.ageRange) : 'N/A'}
                    loading={loading}
                  />
                  <InfoItems
                    title='Weight'
                    value={
                      convertWeight(user?.weight || 0, user?.weightType || WeightType.Kilogram) ===
                      0
                        ? 'N/A'
                        : `${convertWeight(user?.weight || 0, user?.weightType || WeightType.Kilogram)} ${user?.weightType === WeightType.Kilogram ? 'Kg' : 'lb'}`
                    }
                    loading={loading}
                  />{' '}
                  <InfoItems title='Email' value={user?.email} loading={loading} />{' '}
                  <InfoItems
                    title='Gender'
                    value={
                      user?.gender === Gender.NonBinary
                        ? 'Non-Binary'
                        : capitalize(user?.gender || '')
                    }
                    loading={loading}
                  />{' '}
                  <InfoItems
                    title='Height'
                    value={
                      convertHeight(
                        user?.height || 0,
                        user?.heightType || HeightType.Centimeter,
                      ) === 0
                        ? 'N/A'
                        : `${convertHeight(user?.height || 0, user?.heightType || HeightType.Centimeter)} ${
                            user?.heightType === HeightType.Centimeter ? 'cm' : 'ft'
                          }`
                    }
                    loading={loading}
                  />{' '}
                  <InfoItems
                    title='Phone Number'
                    value={formatPhoneNumberIntl(user?.countryCode + ' ' + user?.phoneNumber)}
                    loading={loading}
                  />{' '}
                  <InfoItems title='Location' value={user?.locationName} loading={loading} />
                  <InfoItems
                    title='Languages'
                    value={user?.languages?.map((item) => capitalize(item || '')).join(', ')}
                    loading={loading}
                  />
                </div>
              </CardContent>
            </CardWrapper>
            <CardWrapper className='py-6 px-10'>
              <CardHeader className='flex flex-row items-center justify-between p-0'>
                <h4 className='font-medium text-white'>Physical Info</h4>
                <Button
                  id='edit-profile-btn'
                  size='default'
                  variant='secondary'
                  className='flex items-center gap-1 w-20'
                  disabled={loading}
                  onClick={() => navigate(getRoutes.EditProfile, { state: { form: 2 } })}
                >
                  <Pencil className='w-4 h-4 text-white' />
                  Edit
                </Button>
              </CardHeader>
              <CardContent className='p-0'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  {/* <InfoItems
                    title='Primary Fitness Goals'
                    value={user?.fitnessGoals}
                    loading={loading}
                  /> */}
                  {/* <InfoItems
                    title='Preferred Types of Workouts'
                    value={user?.preferredWorkouts?.map((item) => item?.title).join(', ')}
                    loading={loading}
                  /> */}
                  <InfoItems
                    title='Equipment at home?'
                    value={user?.equipments?.map((item) => item).join(', ')}
                    loading={loading}
                  />
                  {/* <InfoItems
                    title='Prefer to work out time'
                    value={user?.workoutPreference}
                    loading={loading}
                  /> */}
                  <InfoItems
                    title='Fitness Goals and Health Conditions'
                    value={
                      user?.currentEngagements
                        ? `${user.currentEngagements.status ? 'Yes' : 'No'}${user.currentEngagements.description ? `, ${user.currentEngagements.description}` : ''}`
                        : 'No'
                    }
                    loading={loading}
                  />
                </div>
              </CardContent>
            </CardWrapper>
          </>
        ) : userDetails?.role === Role.Trainer || userDetails?.role === Role.Coach ? (
          <TrainerProfile user={user as User} />
        ) : null}
        <CardWrapper className='py-6 px-10'>
          <CardHeader className='flex flex-row items-center justify-between p-0'>
            <h4 className='font-medium text-white'>Manage Account</h4>
          </CardHeader>
          <CardContent className='p-0 flex flex-col gap-5'>
            <div className='flex text-white gap-3 cursor-pointer' onClick={() => setOpen(true)}>
              <LogOut />
              <div className='flex flex-col gap-2'>
                <h5 className='font-semibold'>Logout</h5>
                <h5>
                  When you log out, your data will remain on your device until you delete the app.
                </h5>
              </div>
            </div>
            {userDetails?.role === Role.Trainee && (
              <>
                <div className='w-full border-b border-[#343840]'></div>
                <div
                  className='flex text-white gap-3 cursor-pointer'
                  onClick={() => setDeleteOpen(true)}
                >
                  <Trash2 className='text-[#FF7474]' />
                  <div className='flex flex-col gap-2'>
                    <h5 className='font-semibold text-[#FF7474]'>Delete Account</h5>
                    <h5>
                      When you delete your account, all of your data and friendships will be
                      deleted.
                    </h5>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </CardWrapper>
      </div>
      <DeleteAccount open={deleteOpen} handleClose={() => setDeleteOpen(false)} />
      <CustomDialog
        open={open}
        desc='Logging out ensures the security of your account and personal information.'
        onCancel={() => setOpen(false)}
        title='Are you sure you want to Logout?'
        onConfirm={logout}
      />
    </div>
  );
}

const InfoItems = ({
  title,
  value,
  loading,
}: {
  title: string;
  value?: string | null | number;
  loading: boolean;
}) => {
  return (
    <div className={`${loading ? 'space-y-2' : 'space-y-1'}`}>
      <h5 className='font-semibold text-white'>{title}</h5>
      {loading ? (
        <Skeleton className='w-56 h-4 bg-[#1f1f2e]' />
      ) : (
        <h5 className='text-[#E2E4E8] font-normal'>{value || 'N/A'}</h5>
      )}
    </div>
  );
};

const convertWeight = (weightInGrams: number, weightType: WeightType) => {
  if (weightType === WeightType.Kilogram) {
    return round(weightInGrams / 1000, 1);
  } else if (weightType === WeightType.Pound) {
    return round(weightInGrams * 0.00220462, 1);
  }
  return round(weightInGrams);
};

const convertHeight = (heightInCm: number, heightType: HeightType) => {
  if (heightType === HeightType.Inch) {
    return round(heightInCm / 30.48, 1);
  }
  return round(heightInCm, 1);
};

const SocialMediaLinks = ({ socialMediaLinks }: { socialMediaLinks?: any[] }) => {
  const platformIcons: Record<string, string> = {
    INSTAGRAM: Instagram,
    FACEBOOK: Facebook,
    TWITTER: Twitter,
    YOUTUBE: Youtube,
  };
  return (
    <span className='flex flex-row gap-5 items-center pb-5 pt-2'>
      {socialMediaLinks?.map(({ url, platform }) => {
        const icon = platformIcons[platform];
        return icon ? (
          <a key={platform} href={url} target='_blank' rel='noopener noreferrer'>
            <img src={icon} alt={platform.toLowerCase()} className='w-7 h-7' />
          </a>
        ) : null;
      })}
    </span>
  );
};
